import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from '@mui/material';
import { Check as CheckIcon, X as CloseIcon, Plus as PlusIcon } from 'lucide-react';

export const WorkoutSetInput = ({ exerciseName, sets, onComplete, onAddSet, onSetToggle }) => {
  const [localSets, setLocalSets] = useState(sets || []);
  const [completedSets, setCompletedSets] = useState(new Set());

  const handleSetChange = (index, field, value) => {
    const updated = [...localSets];
    updated[index][field] = value === '' ? '' : Number(value);
    setLocalSets(updated);
  };

  const handleMarkComplete = (index) => {
    const newCompleted = new Set(completedSets);
    if (newCompleted.has(index)) {
      newCompleted.delete(index);
    } else {
      newCompleted.add(index);
    }
    setCompletedSets(newCompleted);
    // Persistir completed en el store en tiempo real
    if (onSetToggle) {
      const updatedSets = localSets.map((set, i) => ({
        ...set,
        completed: newCompleted.has(i),
      }));
      onSetToggle(updatedSets);
    }
  };

  const handleSave = () => {
    const setsToSave = localSets.map((set, i) => ({
      ...set,
      completed: completedSets.has(i),
    }));
    onComplete(setsToSave);
  };

  return (
    <Card sx={{ mb: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {exerciseName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {completedSets.size === localSets.length && localSets.length > 0 && (
              <Box sx={{ color: 'success.main', fontWeight: 'bold' }}>✓</Box>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {localSets.map((set, index) => (
            <Box
              key={index}
              sx={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 1fr 50px',
                gap: 1,
                alignItems: 'center',
                p: 1.5,
                bgcolor: completedSets.has(index) ? 'success.light' : 'grey.50',
                borderRadius: 1,
                transition: 'all 0.2s',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  color: 'text.secondary',
                }}
              >
                Set {index + 1}
              </Box>
              <TextField
                size="small"
                label="Reps"
                type="number"
                value={set.reps}
                onChange={(e) => handleSetChange(index, 'reps', e.target.value)}
                disabled={completedSets.has(index)}
                sx={{ '& .MuiOutlinedInput-root': { py: 0.5 } }}
              />
              <TextField
                size="small"
                label="Peso (kg)"
                type="number"
                value={set.weight}
                onChange={(e) => handleSetChange(index, 'weight', e.target.value)}
                disabled={completedSets.has(index)}
                sx={{ '& .MuiOutlinedInput-root': { py: 0.5 } }}
              />
              <Box
                component="button"
                onClick={() => handleMarkComplete(index)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: completedSets.has(index) ? 'success.main' : 'grey.300',
                  bgcolor: completedSets.has(index) ? 'success.main' : 'transparent',
                  color: completedSets.has(index) ? 'white' : 'text.secondary',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'success.main',
                    bgcolor: 'success.light',
                  },
                }}
              >
                <CheckIcon size={20} />
              </Box>
            </Box>
          ))}
        </Box>

        {onAddSet && (
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={onAddSet}
            sx={{ mt: 1.5 }}
            startIcon={<PlusIcon size={16} />}
          >
            Agregar Set
          </Button>
        )}

        {completedSets.size === localSets.length && localSets.length > 0 && (
          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            sx={{ mt: 1.5 }}
          >
            Siguiente Ejercicio
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export const QuickStartDialog = ({ open, onClose, onStart }) => {
  const [notes, setNotes] = useState('');
  const [skip, setSkip] = useState(false);

  const handleStart = () => {
    onStart({ notes, skip });
    setNotes('');
    setSkip(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Iniciar Entrenamiento</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Notas (cómo te sientes, objetivos de hoy, etc.)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Checkbox
            checked={skip}
            onChange={(e) => setSkip(e.target.checked)}
          />
          <Typography variant="body2">Saltar calentamiento</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleStart} variant="contained">
          Comenzar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
